/**
 * EMR bridge (SHCT-994) — the hospital EMR opens `/emr-bridge?emr_token=…` and this adapter trades the token for a
 * read-only session confined to one patient. Contract: `api-contracts/emr-bridge.md` §B.1·B.1.1·B.4.
 */
import { onIdTokenChanged, signInWithCustomToken } from 'firebase/auth';

import { httpClient } from '@/api/http-client';
import { auth } from '@/api/firebase';
import { endpoints } from '@/api/endpoints';
import { logout } from '@/api/adapters/auth';
import { fetchMe } from '@/api/adapters/my-profile';
import type { PortalRole } from '@/shared/types/auth';

interface ApiSuccess<T> {
  data: T;
}

interface EmrLoginResponse {
  accessToken: string;
  patientPublicId: string;
}

export interface EmrSession {
  patientUid: string;
  email: string;
  name: string;
  role: PortalRole;
}

/** Thrown when the exchanged session is not an EMR one (see {@link openEmrSession}). */
export class EmrSessionNotConfinedError extends Error {
  constructor() {
    super('EMR_SESSION_NOT_CONFINED');
  }
}

/**
 * Exchange the EMR token for a session. The custom token is exchanged IMMEDIATELY — nothing else is awaited between
 * the login response and `signInWithCustomToken` — because a custom token carries only the uid and takes whatever
 * claims the account holds at exchange time (plan D18 accepted risk, review M1). If the doctor's own password login
 * landed in that gap, the exchanged token has no `emr_patient_id` claim: it is an ordinary full session. We refuse
 * to use it — end it and fail like any other bridge failure (plan D22).
 */
async function openEmrSession(emrToken: string): Promise<EmrSession> {
  // X-Refresh-Auth keeps the 401 interceptor out: a refused ticket is not a "your session expired" event.
  const { data } = await httpClient.post<ApiSuccess<EmrLoginResponse>>(
    endpoints.medicalStaff.emrLogin,
    { emrToken },
    { headers: { 'X-Refresh-Auth': '1' } },
  );
  const { accessToken, patientPublicId } = data.data;
  const credential = await signInWithCustomToken(auth, accessToken);

  const { claims } = await credential.user.getIdTokenResult();
  if (claims.emr_patient_id == null) {
    await logout().catch(() => {});
    throw new EmrSessionNotConfinedError();
  }

  try {
    const me = await fetchMe(); // GET /me is on the EMR allow-list (B.1.1)
    return { patientUid: patientPublicId, email: me.email, name: me.name, role: me.role };
  } catch (err) {
    // Signed in but unusable: end the session rather than leave a live token behind a failure screen.
    await logout().catch(() => {});
    throw err;
  }
}

let pending: { token: string; promise: Promise<EmrSession> } | null = null;

/**
 * Run the exchange exactly once per token. React StrictMode mounts effects twice in dev; a second call would be a
 * second login (a second audit row, a second rate-limit hit), so both effects share one promise.
 */
export function startEmrSession(emrToken: string): Promise<EmrSession> {
  if (pending?.token !== emrToken) {
    pending = { token: emrToken, promise: openEmrSession(emrToken) };
  }
  return pending.promise;
}

/** Test hook: forget the memoized exchange. */
export function resetEmrSessionForTest(): void {
  pending = null;
}

/**
 * End the EMR session when the page goes away (window closed, reload, navigation away) — plan D21. `pagehide` gives
 * no time for an async token read, so the latest ID token is kept ready from `onIdTokenChanged`. `keepalive` lets the
 * request outlive the page. Best effort: if it never arrives, the in-memory session is gone with the page anyway and
 * the unused ID token expires within the hour. Returns the cleanup.
 */
export function endEmrSessionOnPageHide(): () => void {
  let idToken: string | null = null;
  const unsubscribe = onIdTokenChanged(auth, (user) => {
    idToken = null;
    void user?.getIdToken().then((t) => {
      idToken = t;
    });
  });
  const onPageHide = () => {
    if (!idToken) return;
    const base = (httpClient.defaults.baseURL ?? '').replace(/\/$/, '');
    void fetch(`${base}${endpoints.medicalStaff.logout}`, {
      method: 'POST',
      keepalive: true,
      // No cookies: an EMR session has none, and one on this browser belongs to another signed-in staff member.
      credentials: 'omit',
      headers: { Authorization: `Bearer ${idToken}`, 'X-Refresh-Auth': '1' },
    }).catch(() => {});
  };
  window.addEventListener('pagehide', onPageHide);
  return () => {
    unsubscribe();
    window.removeEventListener('pagehide', onPageHide);
  };
}
